// Imported through the barrel, exactly like BloodworkRange does — NOT from
// './PanelRange' directly. PanelRange.ts itself imports from '.', so reaching it
// directly makes this module the entry point of the cycle and BloodworkRange
// evaluates `extends PanelRange` before PanelRange exists. Going through the
// barrel means index.ts's declared order always wins, and CustomMarker is
// exported after PanelRange there.
import { PanelRange, PanelField } from '.';
// Gender is a plain enum with no imports of its own, so reaching it directly
// cannot pull the barrel into a cycle the way './PanelRange' would. Section is
// the same shape and reached the same way.
import { Gender } from './Gender';
import { Section } from './Section';
import { MathUtils } from '../utils/MathUtils';

/**
 * The reference-range chart shapes offered for a practitioner-typed marker.
 *
 * Each maps onto flags that already exist on PanelField and are already
 * dispatched by bloodwork-ref-range-analysis.tsx displayChart():
 *
 *   STANDARD          red · orange · yellow · GREEN · yellow · orange · red
 *   LOWER_IS_BETTER                  GREEN (0 -> optMax) · yellow · orange · red
 *   HIGHER_IS_BETTER  red · orange · yellow · GREEN  (nothing flagged high)
 *
 * LabSmarts ships a fourth shape (PanelField.noRedChart — the standard bands
 * with no red on the LOW side, used by Eosinophils, Anion Gap, Indirect
 * Bilirubin, GGT and Reticulocyte Count). It is deliberately NOT offered here:
 * its low side drops to two bands rather than capping the severity of three,
 * which is a distinction too fine to make in a dropdown. Anything stored with
 * an unrecognized chart type falls back to STANDARD via chartFlagsFor().
 */
export enum CustomMarkerChartType {
  STANDARD = 'standard',
  LOWER_IS_BETTER = 'lowerIsBetter',
  HIGHER_IS_BETTER = 'higherIsBetter',
}

/**
 * What a practitioner-typed marker's result IS: a number the report charts, or
 * words the report prints.
 *
 * Plenty of what a lab reports has no number in it. A urinalysis reads
 * "Yellow", "Clear", "Negative"; a differential carries a morphology comment; a
 * stool panel reports "Not detected". None of that can be given a reference
 * range, a bar graph, or a position relative to optimal, and until now the only
 * way to get it onto a LabSmarts report was to not put it there.
 *
 * A TEXT marker is therefore kept out of every numeric path in the app. It is
 * never turned into a PanelMarker, which is what makes that guarantee
 * structural rather than a filter somebody has to remember: with no PanelMarker
 * there is nothing for the Results Overview tally, the Markers Outside Optimal
 * groups, the trend thumbs, the section colors or any pattern engine to pick
 * up, and not one of them had to learn this type exists.
 *
 * Absent means NUMERIC, so every marker saved before this existed charts
 * exactly as it did.
 */
export enum CustomMarkerResultType {
  NUMERIC = 'numeric',
  TEXT = 'text',
}

/**
 * True for a marker whose result is words.
 *
 * Takes anything carrying a resultType — a CustomMarker, a library definition,
 * an entry row — because all three ask this same question and a second copy of
 * the default-to-numeric rule is a second place for it to go wrong.
 */
export function isTextResult(marker?: {
  resultType?: CustomMarkerResultType;
}): boolean {
  return !!marker && marker.resultType === CustomMarkerResultType.TEXT;
}

/**
 * How long a typed result and its expectation may be.
 *
 * A result is a lab's own word or short phrase ("Negative", "Few epithelial
 * cells"), not a note — the Notes tab is where a paragraph belongs, and a
 * paragraph in this cell would blow out the table it prints in on screen, in
 * the PDF and in the comparison report at once.
 */
export const CUSTOM_MARKER_TEXT_MAX_LENGTH = 120;
export const CUSTOM_MARKER_EXPECTED_MAX_LENGTH = 60;

/** Text results are matched the way marker and section names are matched. */
export function normalizeResultText(text?: string): string {
  return (text || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Whether a text result reads as the expected one, or undefined when the
 * question does not arise.
 *
 * Undefined — not false — when either side is blank: a marker with no expected
 * result set has not failed to meet one, and neither has a marker with no
 * result yet. Only a genuine disagreement between two entered strings is worth
 * marking, and callers that treat "unknown" as "differs" would mark every
 * marker the practitioner never set an expectation for.
 *
 * Deliberately an EXACT match once trimmed, lowercased and space-collapsed.
 * Anything looser starts guessing: "Trace" is not "Negative", and a lab that
 * reports "Yellow, cloudy" has said something different from "Yellow" on
 * purpose. The practitioner sets the expectation, so an exact string is one
 * they can always satisfy.
 */
export function matchesExpectedText(
  text?: string,
  expected?: string,
): boolean | undefined {
  const want = normalizeResultText(expected);
  const got = normalizeResultText(text);
  if (!want || !got) {
    return undefined;
  }
  return want === got;
}

/**
 * The chart-type picker's options.
 *
 * Labeled by WHICH DIRECTION IS A CONCERN rather than by the internal chart
 * name, because that is the decision the practitioner is actually making. The
 * description is spelled out in the table's help popover.
 */
export const CUSTOM_MARKER_CHART_TYPE_OPTIONS: {
  value: CustomMarkerChartType;
  label: string;
  description: string;
  /** Worked example, shown in the help popover. One-sided ranges need one. */
  example?: string;
}[] = [
  {
    value: CustomMarkerChartType.STANDARD,
    label: 'High or low is a concern',
    description:
      'The usual chart. Optimal sits in the middle and results are flagged in either direction, the further out the more severe. Use this unless the marker only matters one way.',
    example:
      'Lab range 14–72 U/L: enter Lab Min 14 and Lab Max 72.',
  },
  {
    value: CustomMarkerChartType.LOWER_IS_BETTER,
    label: 'Only high is a concern',
    description:
      'Everything up to the optimal max charts as optimal, and only high results are flagged. This is how LabSmarts charts hs-CRP and TPO antibodies.',
    example:
      'Lab range “≤ 36”: enter Lab Min 0 and Lab Max 36.',
  },
  {
    value: CustomMarkerChartType.HIGHER_IS_BETTER,
    label: 'Only low is a concern',
    description:
      'The mirror image: only low results are flagged, and a high result never is. This is how LabSmarts charts eGFR.',
    example:
      'Lab range “≥ 60”: enter Lab Min 60 and leave Lab Max blank. The rest is calculated from the cutoff (Optimal Min 75, Alarm Min 45).',
  },
];

/**
 * The one dropdown that asks what KIND of result this marker has.
 *
 * "Text result" is offered in the Chart Type picker rather than in a column of
 * its own because it answers the same question the other three answer — how
 * should this marker be drawn — and the honest answer for a urinalysis color is
 * "it isn't". A separate Result Type column would have made every practitioner
 * answer two questions where there is only one, on a table that is already
 * eleven columns wide.
 *
 * Listed last: it is the exception, and the three chart shapes are what the
 * overwhelming majority of markers want.
 */
export const CUSTOM_MARKER_TEXT_OPTION: {
  value: string;
  label: string;
  description: string;
  example?: string;
} = {
  value: CustomMarkerResultType.TEXT,
  label: 'Text marker (no chart)',
  description:
    'For a marker the lab reports in words: a urinalysis color or clarity, “Negative”, a morphology comment. It prints as a line of text in its section instead of a bar graph, and needs no units and no reference range.',
  example:
    'It is left out of Results Overview, Markers Outside Optimal and the trend arrows, because there is no range to judge words against. Set an Expected Result and anything that disagrees is marked on the report.',
};

/** The Chart Type dropdown's full option list, chart shapes plus text. */
export const CUSTOM_MARKER_RESULT_SHAPE_OPTIONS: {
  value: string;
  label: string;
  description: string;
  example?: string;
}[] = [...CUSTOM_MARKER_CHART_TYPE_OPTIONS, CUSTOM_MARKER_TEXT_OPTION];

/** The dropdown value for a row/definition: its chart type, or 'text'. */
export function resultShapeValue(marker: {
  resultType?: CustomMarkerResultType;
  chartType?: CustomMarkerChartType;
}): string {
  return isTextResult(marker)
    ? CustomMarkerResultType.TEXT
    : marker.chartType || CustomMarkerChartType.STANDARD;
}

/**
 * The result of picking `value` in that dropdown, as the fields to merge.
 *
 * Switching to text KEEPS the chart type rather than clearing it, so a marker
 * switched by mistake and switched back is charted the way it was. Nothing
 * reads chartType on a text marker.
 */
export function resultShapeFields(
  value: string,
): { resultType: CustomMarkerResultType; chartType?: CustomMarkerChartType } {
  if (value === CustomMarkerResultType.TEXT) {
    return { resultType: CustomMarkerResultType.TEXT };
  }
  return {
    resultType: CustomMarkerResultType.NUMERIC,
    chartType: (value as CustomMarkerChartType) || CustomMarkerChartType.STANDARD,
  };
}

/**
 * Copy for the "this is a paid feature" popup, shared by My Markers and the
 * per-test table so the two can never say different things.
 *
 * Both links are offered because either kind of practitioner can hit this: a
 * free user needs the pricing page, an existing subscriber needs their account.
 */
export const CUSTOM_MARKER_UPGRADE_MODAL = {
  title: 'Custom Markers Not Available',
  text:
    'Adding your own markers is not enabled on this account. Markers already saved on a test still appear on its report.',
  link: 'https://labsmarts.com/pricing',
  linkText: 'Free user - click here to upgrade.',
  additionalLink: 'https://labsmarts.com/my-account',
  additionalLinkText: 'Paid user - click here to upgrade',
};

/**
 * The report sections a custom marker can be filed under, plus the default of
 * standing on its own.
 *
 * Listed here in the order they appear on the analysis page, which is NOT the
 * order the dropdown shows: that sorts alphabetically, because a list this long
 * is scanned for a name rather than read through (see byLabel in
 * custom-markers-table). Nothing else reads the order, so keeping report order
 * here is only to make the list easy to check against the report.
 *
 * Filing Lipase under Digestion puts its bar graph among the digestion bars,
 * its row in the digestion comparison table, and its value into everything that
 * counts that section's markers: the Results Overview color and tally, the
 * Improved/Worsened arrow, Markers Outside Optimal. A marker the practitioner
 * put in a section counts in that section, on the ranges they entered — the
 * alternative (charting it there but scoring it nowhere) meant a section could
 * read "Optimal" with a red bar sitting inside it.
 *
 * What placement can NEVER do is move a pattern. The Insights panels read named
 * PanelMarker fields (this.glucose, this.tsh...), never the section getters, so
 * no practitioner-typed marker reaches a pattern engine.
 *
 * Two rules make the placement safe, both enforced in
 * BloodworkAnalysis.withCustomMarkers():
 *   - a section with no shipped markers of its own on this test does not get
 *     conjured into existence by a custom marker; the marker falls back to the
 *     Other Lab Results section;
 *   - placed markers are appended AFTER the section's own markers, so the
 *     shipped panel always reads first.
 *
 * Labels mirror the section names in Report Settings' "Include in PDF" list
 * (settings-page.tsx PDF_INCLUSION_SECTIONS) — that is where the practitioner
 * has already learned what each section is called.
 */
export const CUSTOM_MARKER_SECTION_OPTIONS: {
  value: Section;
  label: string;
}[] = [
  { value: Section.RBC,                           label: 'Blood Oxygen & Red Blood Cell Function' },
  { value: Section.IRON,                          label: 'Iron Status & Utilization' },
  { value: Section.WBC,                           label: 'Immune Activity' },
  { value: Section.PLATELETS,                     label: 'Blood Clotting & Flow' },
  { value: Section.GLUCOSE,                       label: 'Glucose Regulation & Metabolic Function' },
  { value: Section.KIDNEY,                        label: 'Kidney Function' },
  { value: Section.PROTEINS,                      label: 'Protein Status' },
  { value: Section.NUTRIENTS,                     label: 'Vitamin & Mineral Status' },
  { value: Section.ELECTROLYTES,                  label: 'Electrolyte Balance' },
  { value: Section.DIGESTION,                     label: 'Digestive & Gut Health' },
  { value: Section.LIVER_BILIARY,                 label: 'Liver Function' },
  { value: Section.LIPIDS,                        label: 'Lipid Balance' },
  { value: Section.INFLAMMATION_OXIDATIVE_STRESS, label: 'Inflammation & Oxidative Stress' },
  { value: Section.THYROID,                       label: 'Thyroid Function' },
  { value: Section.STRESS_ADRENAL,                label: 'Stress & Adrenal Balance' },
  { value: Section.SEX_HORMONE,                   label: 'Hormone Balance' },
  { value: Section.PROSTATE,                      label: 'Prostate Function' },
];

/**
 * The dropdown's default option.
 *
 * Named for the section the marker actually lands in, not for the fact that it
 * wasn't filed anywhere — every other option in that dropdown is a section
 * heading from the report, so this one has to be too, or the practitioner is
 * left guessing where "its own section" ends up. Read straight off the enum so
 * it can never drift from the heading the report prints.
 */
export const CUSTOM_MARKER_OWN_SECTION_LABEL: string = Section.CUSTOM_MARKERS;

/**
 * A section the practitioner named themselves — "IgG Allergens", "Mycotoxins",
 * whatever the lab called the panel LabSmarts has no home for.
 *
 * Kept on the practitioner's profile (UserProfile.customMarkerSections) rather
 * than on the test, so a panel named once is on the dropdown for every client
 * from then on. What lands on the test is the NAME, not a reference to this
 * record — same snapshot rule the marker library follows, so renaming a section
 * cannot retitle a section on a report already issued to a client.
 */
export class CustomMarkerSection {
  /** Stable across renames, so a rename is not a delete plus a create. */
  id: string;
  name: string;

  constructor(section?: Partial<CustomMarkerSection>) {
    if (!section) {
      return;
    }
    this.id = section.id;
    this.name = section.name;
  }
}

/**
 * What the report prints under a practitioner-named section's heading: the
 * name the practitioner gave it, and nothing else.
 *
 * It used to carry a "(Practitioner-Added Section)" suffix, on the reasoning
 * that the client should not read the section as a LabSmarts panel. But the
 * client is not who the caveat helps. They cannot act on it, and printed
 * beside nine unqualified headings it reads as LabSmarts declining to stand
 * behind that one section of their practitioner's report. The provenance the
 * practitioner needs while working is still on screen: every marker they
 * placed carries a diamond in the chart panel, the severity panels and the
 * comparison table (see bloodwork-ref-range-analysis), and the AI export
 * gathers them under practitionerAddedMarkers, ranges described as supplied.
 *
 * Kept as a function rather than inlined at the call sites so the report still
 * has one place that decides how a practitioner's section is titled.
 */
export function practitionerSectionTitle(name: string): string {
  return (name || '').trim();
}

/**
 * Section dropdown values are one field, so a practitioner section is encoded
 * with a prefix a shipped Section value can never carry (every enum value is a
 * plain heading). Decoded on the way into storage, where the two live in
 * separate fields and nothing has to be parsed again.
 */
export const PRACTITIONER_SECTION_PREFIX = 'practitioner:';

export function practitionerSectionOptionValue(name: string): string {
  return `${PRACTITIONER_SECTION_PREFIX}${(name || '').trim()}`;
}

export function practitionerSectionFromOptionValue(
  value?: string,
): string | undefined {
  if (!value || !value.startsWith(PRACTITIONER_SECTION_PREFIX)) {
    return undefined;
  }
  const name = value.slice(PRACTITIONER_SECTION_PREFIX.length).trim();
  return name || undefined;
}

/** The dropdown value for a stored placement, shipped section or practitioner's. */
export function sectionOptionValueFor(stored: {
  section?: Section;
  practitionerSection?: string;
}): string {
  const section = assignableSection(stored?.section);
  if (section) {
    return section;
  }
  const practitionerSection = (stored?.practitionerSection || '').trim();
  return practitionerSection
    ? practitionerSectionOptionValue(practitionerSection)
    : '';
}

/**
 * Section names match the way marker names do: trimmed and case-insensitive, so
 * "IgG Allergens" typed on one test and "IGG allergens" on the next are one
 * section in the comparison report rather than two headings for one panel.
 */
export function practitionerSectionKey(name?: string): string {
  return (name || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * The prefix every practitioner-section key carries, frozen at the literal that
 * was current when these keys shipped.
 *
 * Deliberately NOT Section.CUSTOM_MARKERS, even though that is where the string
 * came from. These keys are PERSISTED — in UserProfile.excludedFromPdfSections
 * and as the `section` on per-test notes — and that section's printed name has
 * already changed three times (see Section.CUSTOM_MARKERS). Built from the enum,
 * every one of those stored keys would be orphaned by the next rename of a
 * heading that is pure display copy. Frozen, a rename cannot reach them at all,
 * which is why LEGACY_SECTION_NAMES does not have to cover them.
 */
const PRACTITIONER_SECTION_KEY_PREFIX = 'Practitioner-Added Markers';

/**
 * The key a practitioner section is collapsed and PDF-excluded under.
 *
 * Prefixed so anything that reasons about these lists can still tell what kind
 * of section it is looking at, and normalized so one panel cannot end up with
 * two collapse states because it was typed "IgG Allergens" on one test and
 * "IGG Allergens" on the next.
 */
export function practitionerSectionStateKey(name: string): string {
  return `${PRACTITIONER_SECTION_KEY_PREFIX}::${practitionerSectionKey(name)}`;
}

/**
 * Why a section name is not allowed, or undefined when it is fine.
 *
 * Shipped headings are refused outright. Allowing "Thyroid Function" would put
 * two sections with one name in the report, and the placement dropdown offers
 * the real one right above.
 */
export function practitionerSectionProblem(
  name: string,
  existing: { id?: string; name?: string }[] = [],
  ignoreId?: string,
): string | undefined {
  const trimmed = (name || '').trim();
  if (!trimmed) {
    return 'Enter a section name.';
  }
  if (trimmed.length > 60) {
    return 'Section names are limited to 60 characters.';
  }
  const key = practitionerSectionKey(trimmed);
  const shipped = [
    ...CUSTOM_MARKER_SECTION_OPTIONS.map(option => option.label),
    ...CUSTOM_MARKER_SECTION_OPTIONS.map(option => String(option.value)),
    CUSTOM_MARKER_OWN_SECTION_LABEL,
  ];
  if (shipped.some(label => practitionerSectionKey(label) === key)) {
    return `LabSmarts already has a section called "${trimmed}" — pick it from the list instead.`;
  }
  if (
    (existing || []).some(
      section =>
        section &&
        section.id !== ignoreId &&
        practitionerSectionKey(section.name) === key,
    )
  ) {
    return `You already have a section called "${trimmed}".`;
  }
  return undefined;
}

/**
 * The example in the Marker Name box, in both the per-test table and My Markers.
 *
 * Must be a marker LabSmarts does NOT ship — it read "Zinc, RBC" until the
 * shipped-name warning went in and started firing on its own placeholder. Pinned
 * by a test against shippedMarkerNamed().
 */
export const MARKER_NAME_PLACEHOLDER_EXAMPLE = 'Lipase';

/**
 * The example in the Units box, which must be the units of the marker named in
 * the box beside it.
 *
 * A hint reading "e.g. mg/L" next to a hint reading "e.g. Lipase" is a worked
 * example that does not work: the two boxes are read together, and units that
 * do not belong to the marker teach the practitioner to distrust both.
 */
export const MARKER_UNITS_PLACEHOLDER_EXAMPLE = 'U/L';

/**
 * The example in the Value box, which must be a result the marker named in the
 * box beside it could actually have.
 *
 * Same rule as the units example: the three boxes are read across as one worked
 * example (Lipase, 50, U/L), so a number that could not be a lipase result
 * teaches the practitioner to distrust the row. 50 U/L sits inside the lab
 * range a lipase is usually reported against.
 */
export const MARKER_VALUE_PLACEHOLDER_EXAMPLE = '50';

/**
 * The same example, for a row whose result is words.
 *
 * "Lipase" is a number, so suggesting it on a text row demonstrates the wrong
 * thing at the moment the practitioner is deciding what this row is for. ANA is
 * the example because it is the one most practitioners have already met that is
 * genuinely reported qualitatively ("Negative", "Positive, 1:80 speckled") —
 * the marker itself explains why the row has no units and no range.
 *
 * Under the same constraint as the numeric example, and pinned by the same
 * test: it must be a marker LabSmarts does NOT ship, or the shipped-name
 * warning fires on the box's own suggestion.
 */
export const MARKER_NAME_PLACEHOLDER_TEXT_EXAMPLE = 'ANA';

/**
 * The shipped marker a typed name is really asking for, if any.
 *
 * Typing "Ferritin" here rather than entering it in the Iron Panel above costs
 * the practitioner everything LabSmarts knows about it — the validated optimal
 * range, the root causes, the patterns it feeds, its place in Markers Outside
 * Optimal — and, if they fill in both, prints the same analyte twice. Nothing
 * used to say so.
 *
 * Deliberately EXACT (after case and whitespace normalization) against the
 * label and short label. A fuzzier match would fire on the near-misses that are
 * genuinely different markers — "Copper, RBC" is not "Copper, Serum", "Vitamin
 * D 1,25" is not "Vitamin D 25-OH" — and a false warning on a marker LabSmarts
 * really doesn't ship is worse than a missed one: it tells the practitioner to
 * go look for a field that isn't there.
 */
export function shippedMarkerNamed(
  name: string,
  fields: PanelField[],
): PanelField | undefined {
  const normalize = (text?: string) =>
    (text || '').trim().toLowerCase().replace(/\s+/g, ' ');
  const target = normalize(name);
  if (!target) {
    return undefined;
  }
  return (fields || []).find(
    field =>
      !field.isHiddenForDisplay &&
      (normalize(field.label) === target ||
        normalize(field.shortLabel) === target),
  );
}

/**
 * A stored section value that is still one of the assignable sections.
 *
 * Anything else — a section retired since the marker was saved, or a stray
 * string — reads as unassigned, which is the harmless direction: the marker
 * charts in Other Lab Results rather than vanishing.
 */
export function assignableSection(section?: string): Section | undefined {
  return CUSTOM_MARKER_SECTION_OPTIONS.some(option => option.value === section)
    ? (section as Section)
    : undefined;
}

/** Default decimal places when none can be derived from what was typed. */
export const CUSTOM_MARKER_DEFAULT_PRECISION = 2;

/*
 * There is deliberately NO cap on custom markers, per test or in the My Markers
 * library.
 *
 * There was one (20 a test, 50 a library), justified as keeping the DynamoDB
 * item clear of 400 KB. The arithmetic never supported it: a marker is ~250
 * bytes, so even a hundred of them is ~25 KB of a 400 KB budget. What the cap
 * did do was stop a practitioner entering a real lab report — a 14-marker IgG
 * mold panel plus CMV, EBV and SDMA is past 20 on its own.
 */

/** The range boundaries, in the order they must be non-decreasing. */
export const CUSTOM_MARKER_RANGE_KEYS = [
  'alarmMin',
  'labMin',
  'optimalMin',
  'optimalMax',
  'labMax',
  'alarmMax',
] as const;

export type CustomMarkerRangeKey = typeof CUSTOM_MARKER_RANGE_KEYS[number];

/**
 * A marker the practitioner typed straight onto ONE test because LabSmarts
 * does not ship it yet. Stored as a nested array on the Bloodwork record
 * (see Bloodwork.customMarkers) — deliberately NOT a reusable, practitioner-level
 * marker library. It never gets a BloodworkRange row, a FurtherConsiderations
 * entry, or a place in any pattern engine.
 */
export class CustomMarker {
  name: string;
  units?: string;
  value?: number;
  /**
   * Whether this marker's result is a number or words. Absent means numeric —
   * see CustomMarkerResultType.
   */
  resultType?: CustomMarkerResultType;
  /**
   * The result, when it is words. Held separately from `value` rather than
   * widening that field to `number | string`: `value` is read by PanelMarker,
   * every chart, the comparison thumbs and the aggregates, and a string
   * arriving in any of them would be a silent NaN rather than a type error.
   */
  textValue?: string;
  /**
   * What the practitioner expects this marker to read, when they set one. A
   * result that disagrees is marked on the report; one that agrees, or one with
   * no expectation set, is printed plainly. Never scored — see
   * matchesExpectedText.
   */
  expectedText?: string;
  chartType?: CustomMarkerChartType;
  /**
   * The report section this marker is filed under, or undefined for its own
   * Other Lab Results section. See CUSTOM_MARKER_SECTION_OPTIONS —
   * placement only, never scoring.
   */
  section?: Section;
  /**
   * A section the practitioner named themselves, stored as the NAME rather than
   * a reference to UserProfile.customMarkerSections: the section heading on a
   * report already issued must not change because the practitioner renamed or
   * deleted the section afterwards. Ignored while `section` holds a shipped
   * section — the dropdown only ever sets one of the two.
   */
  practitionerSection?: string;
  /** Decimal places. Derived on save; never undefined (MathUtils.round(x, undefined) is NaN). */
  precision?: number;

  // Only labMin/labMax are required. Everything else is derived by
  // CustomMarkerRange.init() when blank.
  labMin?: number;
  labMax?: number;
  optimalMin?: number;
  optimalMax?: number;
  alarmMin?: number;
  alarmMax?: number;

  constructor(customMarker?: Partial<CustomMarker>) {
    if (!customMarker) {
      return;
    }
    Object.keys(customMarker).forEach(key => {
      this[key] = customMarker[key];
    });
  }

  /**
   * The synthetic PanelField.id for this marker.
   *
   * Derived from the NAME (not a random id) so the same marker name typed on
   * two different tests resolves to the same key — that is what makes the
   * previous-test blood drop and the comparison-table row matching work with
   * no extra machinery. Renaming a marker on a later test breaks the link,
   * which is an accepted trade for a per-test feature.
   *
   * The `custom:` prefix can never collide with a shipped PanelField.id
   * (all of which are plain camelCase identifiers).
   */
  static keyFor(name: string): string {
    return `custom:${(name || '').trim().toLowerCase().replace(/\s+/g, ' ')}`;
  }

  static isCustomKey(attribute: string): boolean {
    return !!attribute && attribute.startsWith('custom:');
  }

  /**
   * True once the row carries enough to build a range: a name, a lab min, and
   * something to set the width from — normally a lab max, but a one-sided
   * "≥ N" range charted as "only low is a concern" has no lab max to give and
   * derives one from the cut-off alone (see CustomMarkerRange.init()).
   */
  isRenderable?(): boolean {
    if (!this.name || !this.name.trim()) {
      return false;
    }
    // A text marker has nothing to chart and nothing to derive: the words ARE
    // the result, so having them is the whole requirement.
    if (isTextResult(this)) {
      return !!(this.textValue || '').trim();
    }
    if (this.labMin == null) {
      return false;
    }
    if (this.labMax != null) {
      return true;
    }
    return (
      this.chartType === CustomMarkerChartType.HIGHER_IS_BETTER &&
      this.labMin > 0
    );
  }

  /** True when the practitioner has not typed anything at all in the row. */
  isEmptyRow?(): boolean {
    const filled = [
      this.name,
      this.units,
      this.value,
      this.textValue,
      this.expectedText,
      ...CUSTOM_MARKER_RANGE_KEYS.map(key => this[key]),
    ].some(v => v != null && String(v).trim() !== '');
    return !filled;
  }

  getPrecision?(): number {
    return this.precision != null
      ? this.precision
      : CUSTOM_MARKER_DEFAULT_PRECISION;
  }
}

/** One set of reference-range boundaries. Only the lab bounds are ever required. */
export type CustomMarkerRanges = {
  labMin?: number;
  labMax?: number;
  optimalMin?: number;
  optimalMax?: number;
  alarmMin?: number;
  alarmMax?: number;
};

/**
 * A marker in the practitioner's own "My Markers" library
 * (UserProfile.customMarkerLibrary), defined once and picked onto whichever
 * tests need it.
 *
 * The library is an INPUT CONVENIENCE ONLY. Picking a definition copies it into
 * a per-test CustomMarker on Bloodwork.customMarkers, so:
 *   - nothing downstream (report, PDF, comparison) knows the library exists;
 *   - revising a definition can never alter a test already issued to a client;
 *   - deleting one can never blank a saved test.
 */
export class CustomMarkerDefinition {
  /** Stable across renames, so a rename is not a delete + create. */
  id: string;
  name: string;
  units?: string;
  /** Numeric or text — see CustomMarkerResultType. Absent means numeric. */
  resultType?: CustomMarkerResultType;
  /**
   * The expected result for a text marker, remembered so a urinalysis panel
   * defined once carries its expectations onto every test it is picked onto.
   * The RESULT itself is never remembered here: that is what changes per test.
   */
  expectedText?: string;
  chartType?: CustomMarkerChartType;
  precision?: number;
  /** Remembered section placement, copied onto each test the marker is picked onto. */
  section?: Section;
  /** Remembered practitioner-named section, copied onto the test the same way. */
  practitionerSection?: string;

  /** Used whenever no sex-specific variant applies. */
  ranges: CustomMarkerRanges = {};

  /**
   * Optional per-sex overrides, keyed by Gender ('F' / 'M'). Absent or partial
   * is normal — a marker whose range does not vary by sex just has `ranges`.
   */
  rangesByGender?: { [gender: string]: CustomMarkerRanges };

  constructor(definition?: Partial<CustomMarkerDefinition>) {
    if (!definition) {
      return;
    }
    Object.keys(definition).forEach(key => {
      this[key] = definition[key];
    });
    if (!this.ranges) {
      this.ranges = {};
    }
  }
}

/**
 * The range set that applies to a given client.
 *
 * Falls back to the definition's default whenever there is no variant for their
 * sex — including when the client's sex is unrecorded — so a marker with no
 * variants behaves exactly as one defined per-test does.
 */
export function resolveDefinitionRanges(
  definition: CustomMarkerDefinition,
  gender?: string,
): CustomMarkerRanges {
  if (!definition) {
    return {};
  }
  const byGender = definition.rangesByGender || {};
  // A range set with no lab bounds is a half-filled form, not an override.
  const usable = (ranges?: CustomMarkerRanges) =>
    !!ranges && (ranges.labMin != null || ranges.labMax != null);

  // An unrecorded sex resolves as Female, the same assumption
  // PanelRange.findRange makes for every shipped marker.
  const effectiveGender = gender || Gender.FEMALE;
  if (usable(byGender[effectiveGender])) {
    return byGender[effectiveGender];
  }

  if (usable(definition.ranges)) {
    return definition.ranges;
  }

  // No default at all: a marker whose ranges are entirely sex-specific is
  // valid (setting both sexes leaves nobody for a default to cover), so fall
  // back to whichever variant exists rather than charting nothing.
  const fallback = [Gender.FEMALE, Gender.MALE]
    .map(g => byGender[g])
    .find(usable);
  return fallback || definition.ranges || {};
}

/**
 * Turn a library definition into the per-test marker that gets copied onto a
 * Bloodwork record. Deep-copies the ranges, so later edits to the definition
 * cannot reach a test that has already been saved.
 */
export function customMarkerFromDefinition(
  definition: CustomMarkerDefinition,
  gender?: string,
): CustomMarker {
  const ranges = resolveDefinitionRanges(definition, gender);
  if (isTextResult(definition)) {
    // Nothing about a range, a chart or units applies, and copying them across
    // would leave a text marker carrying a lab range it can never be charted
    // against. The result is deliberately NOT copied: that is what the
    // practitioner types on the test.
    return new CustomMarker({
      name: definition.name,
      resultType: CustomMarkerResultType.TEXT,
      expectedText: (definition.expectedText || '').trim() || undefined,
      section: assignableSection(definition.section),
      practitionerSection: assignableSection(definition.section)
        ? undefined
        : (definition.practitionerSection || '').trim() || undefined,
    });
  }
  return new CustomMarker({
    name: definition.name,
    units: definition.units,
    chartType: definition.chartType || CustomMarkerChartType.STANDARD,
    precision: definition.precision,
    section: assignableSection(definition.section),
    practitionerSection: assignableSection(definition.section)
      ? undefined
      : (definition.practitionerSection || '').trim() || undefined,
    labMin: ranges.labMin,
    labMax: ranges.labMax,
    optimalMin: ranges.optimalMin,
    optimalMax: ranges.optimalMax,
    alarmMin: ranges.alarmMin,
    alarmMax: ranges.alarmMax,
  });
}

/**
 * The three kinds of group a marker can be filed into.
 *
 * 'own' is the fallback appendix (Other Lab Results), which is a
 * group in the sense that markers land there, but is not a section anybody
 * named and cannot be renamed or deleted.
 */
export type CustomMarkerSectionKind = 'practitioner' | 'shipped' | 'own';

/**
 * The key two section values are the same section under.
 *
 * Practitioner sections are matched the way their names are matched everywhere
 * else (trimmed, case- and space-insensitive), so "IgG Allergens" typed on one
 * marker and "IGG  allergens" on another are one panel rather than two groups
 * of seven.
 */
export function sectionGroupKey(optionValue?: string): string {
  const section = assignableSection(optionValue);
  if (section) {
    return `section:${section}`;
  }
  const name = practitionerSectionFromOptionValue(optionValue);
  if (name) {
    return `practitioner:${practitionerSectionKey(name)}`;
  }
  return 'own';
}

/**
 * The card order to hold for the rest of this sitting, after a section is
 * renamed mid-entry. Undefined when there is nothing to hold.
 *
 * Section cards are alphabetical, which is right on arrival and wrong the
 * instant a practitioner corrects a name: renaming "Cancer TESTS" to something
 * earlier in the alphabet re-sorted the card above the one they were working
 * in, off the top of the screen, and it read as the section having disappeared.
 * So a rename pins the order it already had, with the renamed section keeping
 * its slot. Nothing about this is persisted — opening Add Test Results again
 * starts from alphabetical, which is the order that is useful for FINDING a
 * section rather than for not losing the one in front of you.
 */
export function sectionOrderAfterRename(
  currentKeys: string[],
  fromKey: string,
  toKey: string,
): string[] | undefined {
  // A correction to the capitalization keys to the same section, so nothing
  // moves and there is nothing to pin. Neither is there when the rename came
  // from somewhere with no card of its own.
  if (fromKey === toKey || !currentKeys.includes(fromKey)) {
    return undefined;
  }
  return currentKeys.map(key => (key === fromKey ? toKey : key));
}

/**
 * Section groups in a pinned order, with anything the pin does not name left
 * in the order it arrived in (alphabetical, from groupBySectionValue).
 *
 * A section created after the pin was taken is one of those: it lands at the
 * end rather than alphabetically, because the pin exists precisely to stop
 * cards moving under the practitioner mid-entry.
 */
export function bySectionOrder<T extends { key: string }>(
  groups: T[],
  order?: string[],
): T[] {
  if (!order || !order.length) {
    return groups;
  }
  const rank = (key: string) => {
    const at = order.indexOf(key);
    return at === -1 ? order.length : at;
  };
  return [...groups].sort((a, b) => rank(a.key) - rank(b.key));
}

/** The heading for a stored section value, shipped or the practitioner's. */
export function sectionDisplayLabel(optionValue?: string): string {
  const section = assignableSection(optionValue);
  if (section) {
    const option = CUSTOM_MARKER_SECTION_OPTIONS.find(
      entry => entry.value === section,
    );
    return option ? option.label : String(section);
  }
  const name = practitionerSectionFromOptionValue(optionValue);
  return name || CUSTOM_MARKER_OWN_SECTION_LABEL;
}

/**
 * A section and whatever is filed under it.
 *
 * This is the unit the practitioner actually thinks in: "the mold panel", not
 * fourteen unrelated markers that happen to share a dropdown value. Nothing new
 * is stored to make it — membership has always been on the marker
 * (`section` / `practitionerSection`), so a group is derived, never persisted.
 *
 * Deriving it rather than storing a marker list on the section is what makes a
 * marker added to the library next month join the panel automatically, and it
 * keeps one source of truth: a stored list could disagree with the placement
 * the marker itself carries.
 *
 * Generic over what is in it because three screens group by section and must
 * agree on the ORDER — My Markers groups its editor rows, the per-test table
 * groups its entry rows, the picker groups library definitions. One ordering
 * rule, three item types.
 */
export type CustomMarkerSectionGroup<T> = {
  /** The stored/dropdown value: '', a Section, or 'practitioner:Name'. */
  value: string;
  /** The heading to print. */
  label: string;
  kind: CustomMarkerSectionKind;
  items: T[];
};

/**
 * Group anything that carries a section value into its sections.
 *
 * Sections the practitioner named come first, then LabSmarts sections that
 * have markers filed into them, then the appendix. Their own panels are what
 * they came to work on; a stray marker filed under Glucose is not, and the
 * appendix is a fallback bucket rather than a panel, so it is pinned last
 * however its name sorts.
 *
 * ALPHABETICAL WITHIN EACH OF THOSE THREE, on the label, for the same reason
 * the Section dropdown sorts that way (see byLabel in custom-markers-table): a
 * list of sections is scanned for a name, and nobody hunting for "Mycotoxins"
 * knows it was the fourth panel they happened to create. Not report order and
 * not creation order — neither is findable.
 *
 * Within a group the caller's order is preserved, which for the library is the
 * order the markers were learned off the lab report they were first typed from.
 * Markers ARE read down as a panel, so they keep the lab's order.
 *
 * `sections` supplies the spelling for the practitioner's own groups (no longer
 * the order), and (with includeEmpty) the sections holding no markers yet — a
 * section named but not yet used still has to be visible, or it cannot be
 * renamed or removed.
 */
export function groupBySectionValue<T>(
  items: T[] | undefined,
  sectionValueOf: (item: T) => string,
  sections?: CustomMarkerSection[],
  options: { includeEmpty?: boolean } = {},
): CustomMarkerSectionGroup<T>[] {
  const groups = new Map<string, CustomMarkerSectionGroup<T>>();

  const ensure = (value: string): CustomMarkerSectionGroup<T> => {
    const key = sectionGroupKey(value);
    const existing = groups.get(key);
    if (existing) {
      return existing;
    }
    const kind: CustomMarkerSectionKind = assignableSection(value)
      ? 'shipped'
      : practitionerSectionFromOptionValue(value)
      ? 'practitioner'
      : 'own';
    const group: CustomMarkerSectionGroup<T> = {
      value,
      label: sectionDisplayLabel(value),
      kind,
      items: [],
    };
    groups.set(key, group);
    return group;
  };

  // Seeded first so the practitioner's list sets the spelling, even for a
  // section whose markers all spell it differently. The order it seeds in no
  // longer matters — the sort below decides that.
  if (options.includeEmpty) {
    (sections || []).forEach(section => {
      const name = (section?.name || '').trim();
      if (name) {
        ensure(practitionerSectionOptionValue(name));
      }
    });
  }

  (items || []).forEach(item => {
    ensure(sectionValueOf(item)).items.push(item);
  });

  const rank: { [K in CustomMarkerSectionKind]: number } = {
    practitioner: 0,
    shipped: 1,
    own: 2,
  };
  // Kind first, then the label — localeCompare so accented and cased names land
  // where a reader expects, matching byLabel in custom-markers-table. Labels
  // are unique within a kind (practitionerSectionProblem refuses a shipped
  // heading), so there is no tie left for a stable sort to break.
  return Array.from(groups.values()).sort((a, b) =>
    rank[a.kind] !== rank[b.kind]
      ? rank[a.kind] - rank[b.kind]
      : a.label.localeCompare(b.label),
  );
}

/** The library grouped into its sections, for the picker and My Markers. */
export function groupDefinitionsBySection(
  library: CustomMarkerDefinition[] | undefined,
  sections?: CustomMarkerSection[],
  options: { includeEmpty?: boolean } = {},
): CustomMarkerSectionGroup<CustomMarkerDefinition>[] {
  return groupBySectionValue(
    (library || []).filter(definition => !!definition),
    definition => sectionOptionValueFor(definition),
    sections,
    options,
  );
}

/**
 * Every library marker filed under one of the given sections, in library order.
 *
 * This is what "add the mold section" and "these sections are on every new
 * test" both resolve through. Resolved against the library LIVE rather than
 * against a stored marker list, so a marker added to the section afterwards is
 * picked up with no second step.
 */
export function definitionsForSectionValues(
  library: CustomMarkerDefinition[] | undefined,
  sectionValues: string[] | undefined,
): CustomMarkerDefinition[] {
  const wanted = new Set(
    (sectionValues || []).map(value => sectionGroupKey(value)),
  );
  if (!wanted.size) {
    return [];
  }
  return (library || []).filter(
    definition =>
      definition &&
      (definition.name || '').trim() &&
      wanted.has(sectionGroupKey(sectionOptionValueFor(definition))),
  );
}

/**
 * Whether a section is set to load onto every new test.
 *
 * Compared through sectionGroupKey rather than by string, so a stored
 * "practitioner:IgG Allergens" still matches after the section is renamed to a
 * different capitalization of itself.
 */
export function isDefaultTestSection(
  defaults: string[] | undefined,
  sectionValue: string,
): boolean {
  const key = sectionGroupKey(sectionValue);
  return (defaults || []).some(value => sectionGroupKey(value) === key);
}

/**
 * Turn a section's "add to every new test" setting on or off.
 *
 * Stored as the section VALUE, not an id, so one list covers all three kinds of
 * group: a section the practitioner named, a LabSmarts section they always file
 * an extra marker into, and the appendix.
 *
 * A practitioner section is re-stored under the spelling passed in, so renaming
 * a defaulted section keeps it defaulted and keeps the stored value readable.
 */
export function withDefaultTestSection(
  defaults: string[] | undefined,
  sectionValue: string,
  on: boolean,
): string[] {
  const key = sectionGroupKey(sectionValue);
  const without = (defaults || []).filter(
    value => sectionGroupKey(value) !== key,
  );
  return on ? [...without, sectionValue] : without;
}

/**
 * Whether ONE marker is set to load onto every new test in its own right.
 *
 * Separate from its section being defaulted, and deliberately so: a
 * practitioner who runs a five-marker lipid panel but only ever adds NON HDL
 * CHOLESTEROL to a test has no section to tick, and ticking the section would
 * bring the other four with it.
 *
 * Keyed by the definition's id rather than its name, because a marker's name is
 * the thing most likely to be corrected and the id is what survives it.
 */
export function isDefaultTestMarker(
  markerIds: string[] | undefined,
  definitionId?: string,
): boolean {
  return !!definitionId && (markerIds || []).includes(definitionId);
}

export function withDefaultTestMarker(
  markerIds: string[] | undefined,
  definitionId: string,
  on: boolean,
): string[] {
  const without = (markerIds || []).filter(id => id !== definitionId);
  return on && definitionId ? [...without, definitionId] : without;
}

/**
 * Everything that loads onto a new test: whole sections, plus markers picked
 * one at a time.
 *
 * Deduped and returned in library order, so a marker whose section is also
 * defaulted appears once and in the place the panel expects it. The two lists
 * are a UNION rather than an override — a section is "and also all of these",
 * never "instead of what you picked".
 */
export function definitionsForDefaults(
  library: CustomMarkerDefinition[] | undefined,
  sectionValues: string[] | undefined,
  markerIds: string[] | undefined,
): CustomMarkerDefinition[] {
  const wantedSections = new Set(
    (sectionValues || []).map(value => sectionGroupKey(value)),
  );
  const wantedMarkers = new Set(markerIds || []);
  if (!wantedSections.size && !wantedMarkers.size) {
    return [];
  }
  return (library || []).filter(
    definition =>
      definition &&
      (definition.name || '').trim() &&
      (wantedMarkers.has(definition.id) ||
        wantedSections.has(sectionGroupKey(sectionOptionValueFor(definition)))),
  );
}

/**
 * Sections whose markers show their full reference range on Add Test Results
 * rather than folding to name, result and units.
 *
 * Folding is right for a panel the practitioner knows by heart (fourteen mold
 * markers on one range), and wrong for one they check as they enter it. Which
 * of those a section is is not something the app can work out, so it asks.
 *
 * Stored the same way defaultTestSections is, and matched the same way, so a
 * rename carries it along.
 */
export function isExpandedTestSection(
  sections: string[] | undefined,
  sectionValue: string,
): boolean {
  const key = sectionGroupKey(sectionValue);
  return (sections || []).some(value => sectionGroupKey(value) === key);
}

export function withExpandedTestSection(
  sections: string[] | undefined,
  sectionValue: string,
  on: boolean,
): string[] {
  const key = sectionGroupKey(sectionValue);
  const without = (sections || []).filter(
    value => sectionGroupKey(value) !== key,
  );
  return on ? [...without, sectionValue] : without;
}

/**
 * Rewrite a renamed section in the defaults list.
 *
 * The list stores names for practitioner sections, so a rename that did not
 * come through here would quietly un-default the panel.
 */
export function renameInDefaultTestSections(
  defaults: string[] | undefined,
  from: string,
  to: string,
): string[] {
  const trimmed = (to || '').trim();
  if (!trimmed) {
    return defaults || [];
  }
  const fromKey = sectionGroupKey(practitionerSectionOptionValue(from));
  return (defaults || []).map(value =>
    sectionGroupKey(value) === fromKey
      ? practitionerSectionOptionValue(trimmed)
      : value,
  );
}

/**
 * Correct a section's name on every library definition filed under it.
 *
 * The My Markers page does this to its own rows when a section is renamed
 * there. Renaming from the Section dropdown while entering a test has to do it
 * too, or the two spellings drift: the profile's list says "Cancer Tests" and
 * the definition still says "Cancer TESTS", so the next test that section is
 * added to arrives filed under a name the list no longer carries.
 *
 * Markers already SAVED on a test are deliberately not covered by this — they
 * store the heading their report was issued under. Definitions are the
 * practitioner's own list, not a record given to anybody.
 */
export function renameDefinitionsSection(
  library: CustomMarkerDefinition[] | undefined,
  from: string,
  to: string,
): CustomMarkerDefinition[] {
  const trimmed = (to || '').trim();
  if (!trimmed) {
    return library || [];
  }
  const fromKey = practitionerSectionKey(from);
  return (library || []).map(definition =>
    practitionerSectionKey(definition?.practitionerSection) === fromKey &&
    (definition?.practitionerSection || '').trim()
      ? new CustomMarkerDefinition({
          ...definition,
          practitionerSection: trimmed,
        })
      : definition,
  );
}

/**
 * Fold markers typed on a test into the practitioner's My Markers library.
 *
 * A marker entered by hand on one client is almost always one they will run
 * again, so saving the test teaches the library its definition — no second trip
 * to My Markers. Existing entries are never touched: the library is where the
 * practitioner curates ranges, so a value typed on a test cannot overwrite one.
 *
 * Returns the new library, or undefined when there is nothing to add — callers
 * use that to skip a pointless profile write.
 *
 * `newId` is injected so this stays pure and testable.
 */
export function learnDefinitionsFromMarkers(
  library: CustomMarkerDefinition[] | undefined,
  markers: CustomMarker[] | undefined,
  newId: () => string,
): CustomMarkerDefinition[] | undefined {
  const existing = library || [];
  const known = new Set(
    existing
      .filter(definition => definition && definition.name)
      .map(definition => CustomMarker.keyFor(definition.name)),
  );

  const learned: CustomMarkerDefinition[] = [];
  (markers || []).forEach(marker => {
    if (!marker || !marker.name || !marker.name.trim()) {
      return;
    }
    const key = CustomMarker.keyFor(marker.name);
    if (known.has(key)) {
      return;
    }
    known.add(key);
    if (isTextResult(marker)) {
      // Learned the same way a numeric marker is, minus everything that only
      // means something for a chart.
      learned.push(
        new CustomMarkerDefinition({
          id: newId(),
          name: marker.name.trim(),
          resultType: CustomMarkerResultType.TEXT,
          expectedText: (marker.expectedText || '').trim() || undefined,
          section: assignableSection(marker.section),
          practitionerSection: assignableSection(marker.section)
            ? undefined
            : (marker.practitionerSection || '').trim() || undefined,
          ranges: {},
        }),
      );
      return;
    }
    learned.push(
      new CustomMarkerDefinition({
        id: newId(),
        name: marker.name.trim(),
        units: marker.units,
        chartType: marker.chartType,
        precision: marker.precision,
        section: assignableSection(marker.section),
        practitionerSection: assignableSection(marker.section)
          ? undefined
          : (marker.practitionerSection || '').trim() || undefined,
        ranges: {
          labMin: marker.labMin,
          labMax: marker.labMax,
          optimalMin: marker.optimalMin,
          optimalMax: marker.optimalMax,
          alarmMin: marker.alarmMin,
          alarmMax: marker.alarmMax,
        },
      }),
    );
  });

  return learned.length ? [...existing, ...learned] : undefined;
}

/**
 * Every custom marker a client has ever had, newest definition of each winning.
 *
 * `tests` must be ordered NEWEST FIRST (as BloodworkEntryStore.clientBloodworks
 * is). Markers are keyed by name, so a marker run in January and skipped in
 * March still comes back — carrying forward only from the latest test would
 * quietly lose it.
 *
 * Everything a client has ever had comes forward: there is no per-test cap to
 * truncate against (see the note above CUSTOM_MARKER_SECTION_OPTIONS' block).
 *
 * Typed structurally rather than against Bloodwork so this module stays out of
 * the Bloodwork <-> CustomMarker import cycle.
 */
export function gatherCustomMarkersAcrossTests(
  tests: {
    id?: string;
    collectionDate?: string;
    customMarkers?: CustomMarker[];
  }[],
  options: { excludeTestId?: string } = {},
): { markers: CustomMarker[]; sourceDates: string[] } {
  const { excludeTestId } = options;
  const byKey = new Map<string, CustomMarker>();
  const sourceDates: string[] = [];

  (tests || []).forEach(test => {
    if (!test || (excludeTestId && test.id === excludeTestId)) {
      return;
    }
    const markers = Array.isArray(test.customMarkers) ? test.customMarkers : [];
    markers.forEach(raw => {
      const customMarker = raw instanceof CustomMarker ? raw : new CustomMarker(raw);
      if (!customMarker.name || !customMarker.name.trim()) {
        return;
      }
      const key = CustomMarker.keyFor(customMarker.name);
      if (byKey.has(key)) {
        return;
      }
      byKey.set(key, customMarker);
      if (test.collectionDate && !sourceDates.includes(test.collectionDate)) {
        sourceDates.push(test.collectionDate);
      }
    });
  });

  return {
    markers: Array.from(byKey.values()),
    sourceDates,
  };
}

/**
 * PanelField flags for a chart type. Spread onto the synthetic PanelField so
 * the existing chart dispatcher and the comparison table's cell coloring
 * (bloodwork-comparison-analysis getClassName) both pick the right shape.
 */
export function chartFlagsFor(
  chartType?: CustomMarkerChartType,
): Partial<PanelField> {
  switch (chartType) {
    case CustomMarkerChartType.LOWER_IS_BETTER:
      return { useRDWChart: true };
    case CustomMarkerChartType.HIGHER_IS_BETTER:
      return { useEGFRChart: true };
    default:
      // Also the landing place for an unrecognized stored value (e.g. the
      // withdrawn 'noLowAlarm'): render the standard chart rather than none.
      return {};
  }
}

/**
 * A PanelRange built on the fly from one CustomMarker row.
 *
 * PanelRange is abstract; BloodworkRange extends it the same way. Inheriting
 * gets us PanelRange.init()'s ±1-std-dev derivation of optimal/alarm/mean from
 * labMin/labMax — the identical fallback every shipped marker already relies on
 * when its DynamoDB row leaves those columns null.
 */
export class CustomMarkerRange extends PanelRange {
  chartType?: CustomMarkerChartType;

  constructor(customMarker?: Partial<CustomMarker>) {
    super();
    if (!customMarker) {
      return;
    }
    this.attribute = CustomMarker.keyFor(customMarker.name);
    this.chartType = customMarker.chartType;
    this.labMin = customMarker.labMin;
    this.labMax = customMarker.labMax;
    this.optimalMin = customMarker.optimalMin;
    this.optimalMax = customMarker.optimalMax;
    this.alarmMin = customMarker.alarmMin;
    this.alarmMax = customMarker.alarmMax;
    this.precision =
      customMarker.precision != null
        ? customMarker.precision
        : CUSTOM_MARKER_DEFAULT_PRECISION;
  }

  init?(): CustomMarkerRange {
    // A "≥ N" lab range gives ONE number, and it is the only number the lab
    // report carries — there is no upper bound and no published optimal to
    // type. But every chart needs a WIDTH: the standard-deviation step that
    // spaces the bands, which PanelRange.init() takes from labMax - labMin.
    //
    // So derive it from the cut-off alone, with the SAME arithmetic the
    // opposite one-sided case already gets for free:
    //
    //   "≤ 36"  ->  lab range [0, 36],  step = (36 - 0) / 4 = 9  = 36 / 4
    //   "≥ 60"  ->  lab max   2 x 60,   step = (120 - 60) / 4 = 15 = 60 / 4
    //
    // Both come out at one quarter of the cut-off, so the two mirror each
    // other. For "≥ 60" that puts Optimal Min at 75, Alarm Min at 45, and the
    // whole high side in the green band.
    //
    // Deliberately NOT eGFR's own stored numbers (60/90/180) — that range is
    // hand-tuned for eGFR specifically and is not a general rule.
    if (
      this.chartType === CustomMarkerChartType.HIGHER_IS_BETTER &&
      this.labMax == null &&
      this.labMin != null &&
      this.labMin > 0
    ) {
      this.labMax = 2 * this.labMin;
    }

    // Collapse the harmless side BEFORE deriving, so a one-directional chart
    // does not get a meaningless boundary on the side nobody is worried about.
    //
    // Without this, TPOAb entered as lab 0–9 with "only high is a concern"
    // derives optimalMin = 2.25 — an absurd floor for an antibody titre — and a
    // result of 1.0 then matches none of the branches in
    // PanelMarker.altChartValue() and falls through to `return -1`.
    //
    // The mirror image is deliberately NOT done for "only low is a concern" —
    // that chart needs its high-side boundaries spread out, not collapsed. See
    // the optimalMax note below.
    if (this.chartType === CustomMarkerChartType.LOWER_IS_BETTER) {
      if (this.optimalMin == null) {
        this.optimalMin = this.labMin;
      }
      if (this.alarmMin == null) {
        this.alarmMin = this.labMin;
      }
    }

    // "Only low is a concern" needs a DIFFERENT optimal max from every other
    // chart, because EGFRReferenceRangeChart's axis is one std dev per tick
    // with an extra interpolated tick between optimalMax and labMax:
    //
    //   alarmMin · labMin · optimalMin · optimalMax · (+1 sd) · labMax · alarmMax
    //
    // so labMax sits TWO std devs above optimalMax there, not one.
    // PanelRange.init()'s default of `labMax - 1 sd` is right for the standard
    // seven-band chart but lands exactly on that interpolated tick here, which
    // printed labMax twice on the axis (the reported "61 ... 61").
    //
    // labMin + 2 sd — the midpoint of the lab range — is the value that tick
    // actually represents, and it reproduces shipped eGFR's stored optimalMax
    // exactly: (60 + 180) / 2 = 120.
    if (
      this.chartType === CustomMarkerChartType.HIGHER_IS_BETTER &&
      this.optimalMax == null &&
      this.labMin != null &&
      this.labMax != null
    ) {
      this.optimalMax = MathUtils.round(
        (this.labMin + this.labMax) / 2,
        this.precision != null
          ? this.precision
          : CUSTOM_MARKER_DEFAULT_PRECISION,
      );
    }

    super.init(
      this.precision != null
        ? this.precision
        : CUSTOM_MARKER_DEFAULT_PRECISION,
    );
    return this;
  }
}

/** The synthetic PanelField for a custom marker row. */
export function customPanelFieldFor(customMarker: CustomMarker): PanelField {
  return new PanelField({
    id: CustomMarker.keyFor(customMarker.name),
    label: (customMarker.name || '').trim(),
    units: customMarker.units,
    precision:
      customMarker.precision != null
        ? customMarker.precision
        : CUSTOM_MARKER_DEFAULT_PRECISION,
    // Practitioner-typed names run long ("Omega-3 Index, RBC"); let the
    // reference-range label wrap instead of widening the label column.
    wrapChartLabel: true,
    // Where the practitioner filed it. Read by BloodworkAnalysis to place the
    // bar graph and by the comparison report to place the row; validated here
    // so a retired section can never strand the marker.
    customSection: assignableSection(customMarker.section),
    // A section of the practitioner's own naming. Only ever consulted when the
    // marker is not filed under a shipped section, so the two can never fight
    // over one marker.
    practitionerSection: assignableSection(customMarker.section)
      ? undefined
      : (customMarker.practitionerSection || '').trim() || undefined,
    ...chartFlagsFor(customMarker.chartType),
  } as PanelField);
}

/**
 * A practitioner-typed marker whose result is words, in the shape the report
 * reads it.
 *
 * The numeric counterpart of this is PanelMarker, and the two are deliberately
 * NOT the same class. PanelMarker exists to answer "where does this value sit
 * in its range" — every consumer of it (the charts, the tallies, the trend
 * thumbs, Markers Outside Optimal, every pattern engine) is built on that
 * question, and there is no honest answer to it for "Negative". Giving text
 * markers their own type means those consumers cannot see them at all, rather
 * than seeing them and having to be taught to look away.
 *
 * Flat and plain on purpose: a label, the words, and where it was filed.
 */
export class CustomTextMarker {
  /** Name-derived, exactly as CustomMarker.keyFor builds it, so the same marker
   *  typed on two tests lines up in the comparison report with no extra work. */
  key: string;
  label: string;
  /** The result on THIS test. Absent on a previous-only marker. */
  text?: string;
  /** What the practitioner said to expect, if anything. */
  expected?: string;
  /** The shipped section it was filed under, if any — placement only. */
  section?: Section;
  /** The section the practitioner named, if any. */
  practitionerSection?: string;
  /** The same marker's result on the previous test, when there is one. */
  previousText?: string;

  constructor(marker?: Partial<CustomTextMarker>) {
    if (!marker) {
      return;
    }
    Object.keys(marker).forEach(key => {
      this[key] = marker[key];
    });
  }

  hasText?(): boolean {
    return !!(this.text || '').trim();
  }

  /**
   * Present on the previous test and not on this one — the text analog of the
   * previous-only bar graph, and marked the same way on the report.
   */
  isPreviousOnly?(): boolean {
    return !this.hasText() && !!(this.previousText || '').trim();
  }

  /** The words to print: this test's, or the previous test's when only it has any. */
  displayText?(): string {
    return (this.hasText() ? this.text : this.previousText || '').trim();
  }

  /**
   * False when the result disagrees with the expectation, true when it meets
   * it, undefined when there is nothing to compare. Read the previous-only
   * result for this too — the words on the report are the ones being judged.
   */
  matchesExpected?(): boolean | undefined {
    return matchesExpectedText(this.displayText(), this.expected);
  }

  /** True only for a genuine disagreement, which is the only case worth marking. */
  differsFromExpected?(): boolean {
    return this.matchesExpected() === false;
  }
}

/**
 * The report-side view of one text marker.
 *
 * Returns undefined for anything that is not a usable text marker, so callers
 * can map-and-filter in one pass rather than repeating the two conditions.
 */
export function customTextMarkerFrom(
  customMarker: CustomMarker,
): CustomTextMarker | undefined {
  if (!isTextResult(customMarker) || !customMarker.isRenderable()) {
    return undefined;
  }
  const section = assignableSection(customMarker.section);
  return new CustomTextMarker({
    key: CustomMarker.keyFor(customMarker.name),
    label: (customMarker.name || '').trim(),
    text: (customMarker.textValue || '').trim(),
    expected: (customMarker.expectedText || '').trim() || undefined,
    section,
    practitionerSection: section
      ? undefined
      : (customMarker.practitionerSection || '').trim() || undefined,
  });
}
